import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal } from './utils';

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map(
      (a) =>
        `  ${a.key}: {\n    fromEmail: '${a.fromEmail}',\n    keyEnv:    '${a.envVarName}',\n  }`,
    )
    .join(',\n\n');

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const schemaDoc = t.schema
          ? `\n    /**\n     * Payload:\n${t.schema.map((f) => `     *   ${f.key}  — ${f.description || f.type}${f.required ? '' : ' (optional)'}`).join('\n')}\n     */`
          : '';
        return `  ${t.camelName}: {${schemaDoc}\n    app:  '${a.key}' as MailAppName,\n    slug: '${t.slug}',\n  }`;
      }),
    )
    .join(',\n\n');

  return `/**
 * mail.config.ts — generated by Mail Service SDK Export
 * Edit to add apps / templates; run the generator again to regenerate.
 */

// ─── Apps ─────────────────────────────────────────────────────────────────────

export const MAIL_APPS = {
${appEntries},
} as const;

export type MailAppName = keyof typeof MAIL_APPS;

// ─── Templates ────────────────────────────────────────────────────────────────

export const MAIL_TEMPLATES = {
${templateEntries},
} as const;

export type MailTemplateName = keyof typeof MAIL_TEMPLATES;
`;
}

function genService(config: ExportConfig): string {
  const typedHelpers = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const methodName = `send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const fields = t.schema
            .map((f) => {
              const tsType =
                f.type === 'number'
                  ? 'number'
                  : f.type === 'boolean'
                    ? 'boolean'
                    : f.type === 'array'
                      ? 'unknown[]'
                      : f.type === 'object'
                        ? 'Record<string, unknown>'
                        : 'string';
              return `    ${f.key}${f.required ? '' : '?'}: ${tsType};`;
            })
            .join('\n');
          return `  static ${methodName}(recipient: string, data: {\n${fields}\n  }, version?: number) {\n    return this.send('${t.camelName}', recipient, data, version);\n  }`;
        }
        return `  static ${methodName}(recipient: string, data: Record<string, unknown> = {}, version?: number) {\n    return this.send('${t.camelName}', recipient, data, version);\n  }`;
      }),
    )
    .join('\n\n');

  return `import { MAIL_APPS, MAIL_TEMPLATES, MailAppName, MailTemplateName } from './mail.config';

const SERVICE_URL = (process.env.${config.serviceUrlVarName} || '').replace(/\\/$/, '');

function getKey(app: MailAppName): string {
  return process.env[MAIL_APPS[app].keyEnv] || '';
}

function headers(app: MailAppName) {
  return { 'Content-Type': 'application/json', 'X-API-KEY': getKey(app) };
}

function isReady(app: MailAppName): boolean {
  return !!(SERVICE_URL && getKey(app));
}

async function post(
  app: MailAppName,
  path: string,
  body: object,
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  if (!isReady(app)) {
    console.warn(\`[Mail] App "\${app}" not configured (missing ${config.serviceUrlVarName} or \${MAIL_APPS[app].keyEnv})\`);
    return { success: false, error: 'Not configured' };
  }
  try {
    const res = await fetch(\`\${SERVICE_URL}\${path}\`, {
      method: 'POST',
      headers: headers(app),
      body: JSON.stringify(body),
    });
    return (await res.json()) as { success: boolean; messageId?: string; error?: string };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(\`[Mail][\${app}] Network error on \${path}:\`, msg);
    return { success: false, error: msg };
  }
}

export class MailService {
  // Send an email. Pass version to pin a specific template version; omit to use the active version.
  static send(template: MailTemplateName, recipient: string, data: Record<string, unknown> = {}, version?: number) {
    const t = MAIL_TEMPLATES[template];
    return post(t.app, '/v1/send', { template_slug: t.slug, recipient, data, ...(version !== undefined ? { version } : {}) });
  }

  static sendRaw(
    app: MailAppName,
    params: { subject: string; html: string; recipient: string; fromName?: string },
  ) {
    return post(app, '/v1/send/raw', {
      subject:   params.subject,
      html:      params.html,
      recipient: params.recipient,
      from_name: params.fromName,
    });
  }

${typedHelpers}
}
`;
}

export const typescriptGenerator: LanguageGenerator = {
  id: 'typescript',
  name: 'TypeScript',
  category: 'full-sdk',
  color: '#3178C6',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail.config.ts', content: genConfig(config), syntax: 'typescript' },
      { filename: 'mail.service.ts', content: genService(config), syntax: 'typescript' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
